import { authApi } from '@shared/api-client';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type ConfirmationStatus = 'waiting' | 'confirming' | 'confirmed' | 'error';

const confirmationErrorMessage = (cause: unknown): string => {
  const apiError = cause as { name?: unknown; message?: unknown };
  if (apiError?.name === 'ApiError' && typeof apiError.message === 'string') {
    return apiError.message;
  }
  if (cause instanceof TypeError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return 'Something went wrong. Please request a new confirmation link.';
};

export const ConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email')?.trim() ?? '';
  const code = searchParams.get('code')?.trim() ?? '';
  const hasConfirmationLink = Boolean(email && code);
  const attemptedConfirmation = React.useRef(false);
  const [status, setStatus] = React.useState<ConfirmationStatus>(
    hasConfirmationLink ? 'confirming' : 'waiting',
  );
  const [error, setError] = React.useState<string | null>(null);
  const [resendNotice, setResendNotice] = React.useState<string | null>(null);
  const [isResending, setIsResending] = React.useState(false);

  // Cognito puts the confirmation code in the email link. Confirm it as soon
  // as the link opens, matching Drone Bundle's one-click verification flow.
  React.useEffect(() => {
    if (!hasConfirmationLink || attemptedConfirmation.current) {
      return;
    }

    attemptedConfirmation.current = true;
    void authApi
      .confirmSignUp({ email, code })
      .then(() => setStatus('confirmed'))
      .catch((cause: unknown) => {
        setError(confirmationErrorMessage(cause));
        setStatus('error');
      });
  }, [code, email, hasConfirmationLink]);

  const handleResend = async () => {
    if (!email) {
      return;
    }

    setError(null);
    setResendNotice(null);
    setIsResending(true);

    try {
      await authApi.resendConfirmation({ email });
      setResendNotice('A new confirmation link is on its way.');
      setStatus('waiting');
    } catch (cause) {
      setError(confirmationErrorMessage(cause));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Confirm your email</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {status === 'confirming' ? (
          <Alert title="Confirming your email">
            This will only take a moment.
          </Alert>
        ) : null}

        {status === 'confirmed' ? (
          <>
            <Alert tone="success" title="Email confirmed">
              Your GainSignal account is ready.
            </Alert>
            <Button asChild className="w-full">
              <Link to="/signin">Sign in to your account</Link>
            </Button>
          </>
        ) : null}

        {status === 'waiting' ? (
          <Alert title="Check your inbox">
            {email
              ? `We sent a confirmation link to ${email}. Click it to finish creating your account.`
              : 'Open the confirmation link in your email to finish creating your account.'}
          </Alert>
        ) : null}

        {status === 'error' ? (
          <Alert tone="error" title="We could not confirm this link">
            {error}
          </Alert>
        ) : error ? (
          <Alert tone="error">{error}</Alert>
        ) : null}

        {resendNotice ? <Alert tone="success">{resendNotice}</Alert> : null}

        {status !== 'confirmed' ? (
          <div className="flex items-center justify-between text-sm">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              onClick={handleResend}
              disabled={!email}
              loading={isResending}
            >
              Send a new link
            </Button>
            <Link
              to="/signin"
              className="text-ink-muted underline-offset-4 hover:text-ink hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
